import { createElement, FC } from 'react';
import PureTable from './PureTable';
import { BasePureTableAction, ExtendedActionProps, PureTableAction } from './PureTable.types';
import { classes } from '../../../lib/utils/cssUtils';
import styles from './PureTable.module.scss';
import ThreeDotsIcon from '../../../lib/icons/ThreeDotsIcon';
import Dropdown, { DropdownItem } from '../Dropdown/Dropdown';
import { useAttributeLabel } from '../../../lib/hooks/labels';
import { ATTRIBUTES } from '../../../lib/constants/attributes';
import { TABLE_LABELS } from '../../../lib/constants/labelKeys';
import { getBaseTableAction } from './PureTable.utils';

export type ExtendedTableActionsProps = {
  id: string;
  actions: { type: PureTableAction; action?: (id: string) => void }[];
};

const tableActions: Partial<Record<BasePureTableAction, FC<ExtendedActionProps>>> = {
  delete: PureTable.DeleteAction,
  openDetails: PureTable.OpenDetails,
  download: PureTable.DownloadAction,
};

const ExtendedTableActions = ({ id, actions }: ExtendedTableActionsProps) => {
  const attributes = useAttributeLabel([ATTRIBUTES.TITLE, ATTRIBUTES.ARIA_LABEL], TABLE_LABELS.SHOW_MORE_ACTIONS);

  if (!actions.length) {
    return null;
  }

  if (actions.length === 1) {
    const { type, action } = actions[0];
    const Comp = tableActions[getBaseTableAction(type)];

    return Comp ? createElement(Comp, { key: `${id}-${type}`, titleKey: type, id, onClick: action }) : null;
  }

  return (
    <Dropdown
      SelectDisplayProps={attributes}
      className={classes(styles.actionsDropdown, styles.actionButtonMarker)}
      IconComponent={ThreeDotsIcon}
      classes={{ icon: styles.dropdownIcon, select: styles.dropdownSelectInput, iconOpen: styles.iconOpened }}
      MenuProps={{ classes: { paper: styles.dropdownOptionsContainer } }}
      value=""
    >
      {actions.map(({ type, action }) => {
        const Comp = tableActions[getBaseTableAction(type)];

        if (!Comp) {
          return null;
        }

        return (
          <DropdownItem key={`${id}-${type}`} onClick={() => action?.(id)}>
            {createElement(Comp, {
              titleKey: type,
              id,
              withLabel: true,
              asDiv: true,
            })}
          </DropdownItem>
        );
      })}
    </Dropdown>
  );
};

export default ExtendedTableActions;
